import React from 'react';
import { arrayOf, bool, shape, string } from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Icon, Tag, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import DetailRow from '@components/DetailRow';
import { whatTypeIcons, TRANSITION_NEXT, FEATURE_WEBLINK, whatLabelTypes } from '@constants';
import { faStethoscope, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { faUsers } from '@fortawesome/pro-regular-svg-icons';
import { useContentRef } from '@hooks/useContentRefs';
import cn from 'classnames';
import { pathwayRuleType } from '../../propTypes';
import './style.less';

function RuleInformation({ rule, roles, isIndexEventRule }) {
  const { t } = useTranslation();
  const hasRoles = roles?.length > 0;
  const careTeamHeading = t('pathways:ProcedurePathways.wizard.careTeam.header');

  const renderRolePermission = (role, index) => {
    const permissionLabel = role.permissionType
      ? t(`pathways:ProcedureRules.ruleTooltip.permissionTypes.${role.permissionType}`)
      : '';
    return (
      <div key={`${role.roleName}-${role.permissionType}-${index}`}>
        {role.roleName} - {permissionLabel}
      </div>
    );
  };

  return (
    <Form layout="vertical">
      <DetailRow
        className="rule-tooltip-row"
        label={t('pathways:ProcedureRules.ruleTooltip.name')}
        value={rule.name}
      />
      <DetailRow
        className="rule-tooltip-row"
        label={t('pathways:ProcedureRules.ruleTooltip.description')}
        value={rule.description}
      />
      <DetailRow
        className="rule-tooltip-row"
        label={t('pathways:ProcedureRules.ruleTooltip.contentTitle')}
        value={rule.whatDetail.title || rule.whatDetail.name || t('common:notApplicable')}
      />
      {hasRoles ? (
        <DetailRow
          className="rule-tooltip-row"
          label={careTeamHeading}
          value={<div>{roles.map(renderRolePermission)}</div>}
        />
      ) : null}
      {isIndexEventRule ? (
        <DetailRow
          className="rule-tooltip-row"
          label={t('pathways:ProcedureRules.ruleTooltip.indexEventName')}
          value={rule.whenDetail.name}
        />
      ) : null}
    </Form>
  );
}

function IndexEventTriggeredRuleIcon({ what }) {
  const { t } = useTranslation();
  const IconComponent = TRANSITION_NEXT === what ? Icon : FontAwesomeIcon;
  const iconProps =
    TRANSITION_NEXT === what ? { component: whatTypeIcons[what] } : { icon: whatTypeIcons[what] };

  return (
    <Tooltip title={t(whatLabelTypes[what])}>
      <IconComponent {...iconProps} className="rule-what-icon" />
    </Tooltip>
  );
}

const RuleTagWrapper = ({ rule, contentType, className, roles }) => {
  const hasRoles = Array.isArray(roles) && roles.length > 0;

  return (
    <div className="rule-tag-wrapper" key={rule.id}>
      <Tag className={className} key={rule.id}>
        {<IndexEventTriggeredRuleIcon what={contentType} />}
        <span className="rule-tag-name">{rule.name}</span>
        {rule.audienceType === 'clinician' ? (
          <FontAwesomeIcon icon={faStethoscope} className="rule-audience-type-icon" />
        ) : null}
        {rule.isActioned === true ? (
          <FontAwesomeIcon icon={faCheckCircle} className="rule-actioned-icon" />
        ) : null}
        {hasRoles ? <FontAwesomeIcon icon={faUsers} className="rule-role-icon" /> : null}
      </Tag>
    </div>
  );
};

const DeletedTriggeredRuleTag = ({ rule, isIndexEventRule, t, contentType, className, roles }) => {
  const { loading, isDeleted: contentIsDeleted, url } = useContentRef(
    rule.content || rule.whatDetail?.content,
  );

  if (loading) {
    return null;
  }

  const finalClassName = contentIsDeleted ? cn(className, 'content-deleted') : className;
  const ruleTagWrapper = (
    <RuleTagWrapper
      rule={rule}
      contentType={contentType}
      className={finalClassName}
      roles={roles}
    />
  );

  return (
    <Tooltip
      title={<RuleInformation rule={rule} roles={roles} isIndexEventRule={isIndexEventRule} />}
    >
      {contentIsDeleted ? ruleTagWrapper : <Link to={url}>{ruleTagWrapper}</Link>}
    </Tooltip>
  );
};

function RuleTag({ rule, tagClassName, triggered, isIndexEventRule, disableLink, roles }) {
  const { t } = useTranslation();
  const history = useHistory();

  const className = cn(tagClassName, { triggered });
  const contentType =
    rule.what === FEATURE_WEBLINK ? rule.whatDetail.type || FEATURE_WEBLINK : rule.what;

  if (triggered && rule.isDeleted) {
    return (
      <DeletedTriggeredRuleTag
        rule={rule}
        isIndexEventRule={isIndexEventRule}
        t={t}
        contentType={contentType}
        className={className}
        roles={roles}
      />
    );
  }

  const ruleTagWrapper = (
    <RuleTagWrapper rule={rule} contentType={contentType} className={className} roles={roles} />
  );

  const wrappedContent = disableLink ? (
    <span>{ruleTagWrapper}</span>
  ) : (
    <Link to={`/procedure/rules/${rule.id}/${history.location.pathname.split('/')[3]}`}>
      {ruleTagWrapper}
    </Link>
  );

  return (
    <Tooltip
      overlayClassName="rule-tooltip"
      title={<RuleInformation rule={rule} roles={roles} isIndexEventRule={isIndexEventRule} />}
    >
      {wrappedContent}
    </Tooltip>
  );
}

RuleTag.propTypes = {
  rule: pathwayRuleType.isRequired,
  isIndexEventRule: bool,
  triggered: bool,
  tagClassName: string,
  disableLink: bool,
  roles: arrayOf(
    shape({
      roleName: string,
      permissionType: string,
    }),
  ),
};

RuleTag.defaultProps = {
  isIndexEventRule: false,
  triggered: false,
  tagClassName: '',
  disableLink: false,
  roles: [],
};

export default RuleTag;

export { IndexEventTriggeredRuleIcon };
